import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  TextField,
  IconButton,
  Drawer,
  Tabs,
  Tab,
  Tooltip,
  Divider,
  Link,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Replay as ReplayIcon,
  PlayArrow as PlayArrowIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import api from '../api';
import { useAuth } from '../AuthContext';

function shortAddr(a) {
  if (!a) return '—';
  const s = String(a);
  return s.length > 14 ? `${s.slice(0, 6)}…${s.slice(-4)}` : s;
}

function truncate(s, n = 64) {
  if (s == null) return '—';
  const str = String(s);
  return str.length > n ? `${str.slice(0, n)}…` : str;
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

function MetadataDetailDrawer({ row, open, onClose }) {
  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 600, maxWidth: '90vw', p: 3 } }}>
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">NFT Metadata</Typography>
          <Button onClick={onClose} size="small">Close</Button>
        </Stack>
        {!row ? (
          <Alert severity="info">No row selected.</Alert>
        ) : (
          <>
            <Stack spacing={1}>
              <Typography variant="body2"><strong>Collection:</strong> {row.collectionId}</Typography>
              <Typography variant="body2">
                <strong>Contract:</strong>{' '}
                <code>{row.contractAddress}</code>
              </Typography>
              <Typography variant="body2"><strong>Token ID:</strong> {row.tokenId}</Typography>
              <Typography variant="body2"><strong>Name:</strong> {row.name || '—'}</Typography>
              <Typography variant="body2"><strong>Description:</strong> {row.description || '—'}</Typography>
              {row.image && (
                <Typography variant="body2">
                  <strong>Image:</strong>{' '}
                  <Link href={row.image} target="_blank" rel="noopener">{truncate(row.image, 80)}</Link>
                </Typography>
              )}
              {row.animationUrl && (
                <Typography variant="body2">
                  <strong>Animation:</strong>{' '}
                  <Link href={row.animationUrl} target="_blank" rel="noopener">{truncate(row.animationUrl, 80)}</Link>
                </Typography>
              )}
              {row.externalUrl && (
                <Typography variant="body2">
                  <strong>External URL:</strong>{' '}
                  <Link href={row.externalUrl} target="_blank" rel="noopener">{truncate(row.externalUrl, 80)}</Link>
                </Typography>
              )}
              {row.model && (
                <Typography variant="body2"><strong>Model:</strong> {row.model}</Typography>
              )}
              <Typography variant="body2">
                <strong>Created:</strong> {formatDate(row.createdAt)} · <strong>Updated:</strong> {formatDate(row.updatedAt)}
              </Typography>
            </Stack>

            <Divider />

            <Box>
              <Typography variant="subtitle2" gutterBottom>Image preview</Typography>
              {row.image ? (
                <img
                  src={row.image}
                  alt={row.name || 'nft'}
                  style={{ maxWidth: '100%', maxHeight: 320, borderRadius: 8, objectFit: 'contain', background: '#000' }}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">No image URL.</Typography>
              )}
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" gutterBottom>Attributes ({Array.isArray(row.attributes) ? row.attributes.length : 0})</Typography>
              {Array.isArray(row.attributes) && row.attributes.length > 0 ? (
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {row.attributes.map((a, i) => (
                    <Chip
                      key={i}
                      size="small"
                      label={`${a.trait_type ?? a.traitType ?? 'trait'}: ${a.value ?? ''}`}
                    />
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">No attributes.</Typography>
              )}
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" gutterBottom>Raw JSON</Typography>
              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#0d1117', maxHeight: 320, overflow: 'auto' }}>
                <pre style={{ margin: 0, fontSize: 12, color: '#c9d1d9' }}>
                  {JSON.stringify(row, null, 2)}
                </pre>
              </Paper>
            </Box>
          </>
        )}
      </Stack>
    </Drawer>
  );
}

function ExistingMetadataTab() {
  const { user } = useAuth();
  const canResolve = user?.role === 'admin' || user?.role === 'super_admin';
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [contract, setContract] = useState('');
  const [collectionId, setCollectionId] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedRow, setSelectedRow] = useState(null);

  const queryKey = useMemo(
    () => ['nftMetadataRows', page + 1, rowsPerPage, contract, collectionId, search],
    [page, rowsPerPage, contract, collectionId, search]
  );

  const { data, isLoading, isFetching, refetch, isError, error } = useQuery({
    queryKey,
    queryFn: async () => {
      const r = await api.get('/nft-metadata/rows', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          contract: contract || undefined,
          collectionId: collectionId || undefined,
          search: search || undefined,
        },
      });
      return r.data;
    },
    placeholderData: keepPreviousData,
    staleTime: 10_000,
  });

  const refreshMutation = useMutation({
    mutationFn: async (row) => {
      const r = await api.post('/nft-metadata/refresh-row', {
        contractAddress: row.contractAddress,
        tokenId: row.tokenId,
      });
      return r.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['nftMetadataRows'] }),
  });

  const rows = data?.data || [];
  const total = data?.pagination?.total || 0;

  return (
    <Stack spacing={2}>
      <Card>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <TextField
              label="Contract address"
              size="small"
              fullWidth
              value={contract}
              onChange={(e) => { setContract(e.target.value.trim()); setPage(0); }}
              placeholder="0x…"
            />
            <TextField
              label="Collection id"
              size="small"
              fullWidth
              value={collectionId}
              onChange={(e) => { setCollectionId(e.target.value.trim()); setPage(0); }}
              placeholder="paradise-genesis-collection-v2"
            />
            <TextField
              label="Search (name / description / tokenId)"
              size="small"
              fullWidth
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { setSearch(searchInput.trim()); setPage(0); }
              }}
            />
            <Button
              variant="outlined"
              onClick={() => { setSearch(searchInput.trim()); setPage(0); }}
            >
              Search
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => refetch()}
              disabled={isFetching}
            >
              Refresh
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {isError && <Alert severity="error">{error?.response?.data?.message || error.message}</Alert>}

      <Card>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Collection</TableCell>
                <TableCell>Contract</TableCell>
                <TableCell>Token ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Image</TableCell>
                <TableCell>Attrs</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={8} align="center"><CircularProgress size={20} /></TableCell>
                </TableRow>
              )}
              {!isLoading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary">No metadata rows found.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.collectionId}</TableCell>
                  <TableCell>
                    <Tooltip title={r.contractAddress}><code>{shortAddr(r.contractAddress)}</code></Tooltip>
                  </TableCell>
                  <TableCell>{r.tokenId}</TableCell>
                  <TableCell>{truncate(r.name, 32)}</TableCell>
                  <TableCell>
                    {r.image ? (
                      <Link href={r.image} target="_blank" rel="noopener">{truncate(r.image, 32)}</Link>
                    ) : '—'}
                  </TableCell>
                  <TableCell>{Array.isArray(r.attributes) ? r.attributes.length : 0}</TableCell>
                  <TableCell>{formatDate(r.updatedAt)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="View details">
                      <IconButton size="small" onClick={() => setSelectedRow(r)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {canResolve ? (
                      <Tooltip title="Re-resolve metadata for this mint (pipeline row must be mint_confirmed)">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => refreshMutation.mutate(r)}
                            disabled={refreshMutation.isPending}
                          >
                            <ReplayIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_e, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Card>

      <MetadataDetailDrawer
        row={selectedRow}
        open={!!selectedRow}
        onClose={() => setSelectedRow(null)}
      />
    </Stack>
  );
}

function MissingMetadataTab() {
  const { user } = useAuth();
  const canBulkBackfill = user?.role === 'super_admin';
  const canResolve = user?.role === 'admin' || user?.role === 'super_admin';
  const queryClient = useQueryClient();
  const [contract, setContract] = useState('');
  const [collectionId, setCollectionId] = useState('');
  const [limit, setLimit] = useState(500);
  const [confirmBackfill, setConfirmBackfill] = useState(false);

  const queryKey = useMemo(
    () => ['nftMetadataMissing', contract, collectionId, limit],
    [contract, collectionId, limit]
  );

  const { data, isLoading, isFetching, refetch, isError, error } = useQuery({
    queryKey,
    queryFn: async () => {
      const r = await api.get('/nft-metadata/missing', {
        params: {
          contract: contract || undefined,
          collectionId: collectionId || undefined,
          limit,
        },
      });
      return r.data;
    },
    staleTime: 5_000,
  });

  const backfillMutation = useMutation({
    mutationFn: async (payload) => {
      const r = await api.post('/nft-metadata/backfill', payload);
      return r.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nftMetadataMissing'] });
      queryClient.invalidateQueries({ queryKey: ['nftMetadataRows'] });
    },
  });

  const dryRunMutation = useMutation({
    mutationFn: async () => {
      const r = await api.post('/nft-metadata/backfill', {
        contract: contract || undefined,
        collectionId: collectionId || undefined,
        limit,
        dryRun: true,
      });
      return r.data;
    },
  });

  const refreshMutation = useMutation({
    mutationFn: async (row) => {
      const r = await api.post('/nft-metadata/refresh-row', { mintRequestId: row.mintRequestId });
      return r.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nftMetadataMissing'] });
      queryClient.invalidateQueries({ queryKey: ['nftMetadataRows'] });
    },
  });

  const rows = data?.rows || [];
  const count = data?.count ?? 0;

  return (
    <Stack spacing={2}>
      <Card>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>Find confirmed mints missing metadata</Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <TextField
              label="Contract address (optional)"
              size="small"
              fullWidth
              value={contract}
              onChange={(e) => setContract(e.target.value.trim())}
              placeholder="0x…"
            />
            <TextField
              label="Collection id (optional)"
              size="small"
              fullWidth
              value={collectionId}
              onChange={(e) => setCollectionId(e.target.value.trim())}
            />
            <TextField
              label="Scan limit"
              size="small"
              type="number"
              value={limit}
              onChange={(e) => setLimit(Math.max(1, Math.min(2000, parseInt(e.target.value, 10) || 500)))}
              sx={{ width: 140 }}
              inputProps={{ min: 1, max: 2000 }}
            />
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => refetch()}
              disabled={isFetching}
            >
              Scan
            </Button>
            {canBulkBackfill ? (
              <>
                <Button
                  variant="outlined"
                  disabled={count === 0 || dryRunMutation.isPending}
                  onClick={() => dryRunMutation.mutate()}
                >
                  {dryRunMutation.isPending ? 'Dry run…' : 'Dry run (response preview)'}
                </Button>
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<PlayArrowIcon />}
                  disabled={count === 0 || backfillMutation.isPending}
                  onClick={() => setConfirmBackfill(true)}
                >
                  {backfillMutation.isPending ? 'Backfilling…' : `Backfill ${count} row${count === 1 ? '' : 's'}`}
                </Button>
              </>
            ) : null}
          </Stack>
          {!canBulkBackfill ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              Bulk dry-run and backfill require super_admin. Admins can still scan this list and re-resolve rows one-by-one below.
            </Alert>
          ) : null}
          {confirmBackfill && (
            <Alert
              severity="warning"
              sx={{ mt: 2 }}
              action={
                <Stack direction="row" spacing={1}>
                  <Button size="small" onClick={() => setConfirmBackfill(false)}>Cancel</Button>
                  <Button
                    size="small"
                    color="warning"
                    variant="contained"
                    onClick={() => {
                      setConfirmBackfill(false);
                      backfillMutation.mutate({
                        contract: contract || undefined,
                        collectionId: collectionId || undefined,
                        limit,
                      });
                    }}
                  >
                    Run backfill
                  </Button>
                </Stack>
              }
            >
              This re-runs the metadata resolver for all {count} confirmed mint rows currently lacking NftMetadata.
              Filters above are applied.
            </Alert>
          )}
        </CardContent>
      </Card>

      {isError && <Alert severity="error">{error?.response?.data?.message || error.message}</Alert>}

      {dryRunMutation.data?.dryRun && (
        <Alert severity="info" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          Dry run: would process {dryRunMutation.data.count} row(s).
          Sample: {(dryRunMutation.data.rows?.slice(0, 5) ?? [])
            .map((x) => `${x.contractAddress}/${x.tokenId}`)
            .join(', ') || '(none)'}
        </Alert>
      )}
      {dryRunMutation.isError && (
        <Alert severity="error">{dryRunMutation.error?.response?.data?.message || dryRunMutation.error?.message}</Alert>
      )}

      {backfillMutation.data && (
        <Alert severity={backfillMutation.data.failed > 0 ? 'warning' : 'success'}>
          Backfill done — attempted: {backfillMutation.data.attempted ?? 0},
          succeeded: {backfillMutation.data.succeeded ?? 0},
          failed: {backfillMutation.data.failed ?? 0}.
          {Array.isArray(backfillMutation.data.failures) && backfillMutation.data.failures.length > 0 && (
            <Box component="pre" sx={{ mt: 1, fontSize: 11, maxHeight: 200, overflow: 'auto' }}>
              {JSON.stringify(backfillMutation.data.failures, null, 2)}
            </Box>
          )}
        </Alert>
      )}
      {backfillMutation.isError && (
        <Alert severity="error">{backfillMutation.error?.response?.data?.message || backfillMutation.error?.message}</Alert>
      )}

      <Card>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Collection</TableCell>
                <TableCell>Contract</TableCell>
                <TableCell>Token ID</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Source contract</TableCell>
                <TableCell>Source token</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={8} align="center"><CircularProgress size={20} /></TableCell>
                </TableRow>
              )}
              {!isLoading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No confirmed mints missing metadata in the scanned window.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {rows.map((r) => (
                <TableRow key={r.mintRequestId} hover>
                  <TableCell>{r.destinationCollection}</TableCell>
                  <TableCell>
                    <Tooltip title={r.contractAddress}><code>{shortAddr(r.contractAddress)}</code></Tooltip>
                  </TableCell>
                  <TableCell>{r.tokenId}</TableCell>
                  <TableCell>{r.source || '—'}</TableCell>
                  <TableCell>
                    <Tooltip title={r.sourceContract || ''}><code>{shortAddr(r.sourceContract)}</code></Tooltip>
                  </TableCell>
                  <TableCell>{r.sourceTokenId || '—'}</TableCell>
                  <TableCell>{formatDate(r.updatedAt)}</TableCell>
                  <TableCell align="right">
                    {canResolve ? (
                      <Tooltip title="Resolve metadata for this row only">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => refreshMutation.mutate(r)}
                            disabled={refreshMutation.isPending}
                          >
                            <ReplayIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Stack>
  );
}

function StaleIpfsRewriteTab() {
  const { user } = useAuth();
  const canBulkRewrite = user?.role === 'super_admin';
  const queryClient = useQueryClient();
  const [contract, setContract] = useState('');
  const [collectionId, setCollectionId] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [limit, setLimit] = useState(500);
  const [confirmRewrite, setConfirmRewrite] = useState(false);

  const queryKey = useMemo(
    () => ['nftMetadataStaleIpfs', contract, collectionId, page + 1, rowsPerPage],
    [contract, collectionId, page, rowsPerPage],
  );

  const { data, isLoading, isFetching, refetch, isError, error } = useQuery({
    queryKey,
    queryFn: async () => {
      const r = await api.get('/nft-metadata/stale-ipfs', {
        params: {
          contract: contract || undefined,
          collectionId: collectionId || undefined,
          page: page + 1,
          limit: rowsPerPage,
        },
      });
      return r.data;
    },
    placeholderData: keepPreviousData,
    staleTime: 5_000,
  });

  const rewriteMutation = useMutation({
    mutationFn: async (payload) => {
      const r = await api.post('/nft-metadata/rewrite-media', payload);
      return r.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nftMetadataStaleIpfs'] });
      queryClient.invalidateQueries({ queryKey: ['nftMetadataRows'] });
    },
  });

  const dryRunMutation = useMutation({
    mutationFn: async () => {
      const r = await api.post('/nft-metadata/rewrite-media', {
        contract: contract || undefined,
        collectionId: collectionId || undefined,
        limit,
        dryRun: true,
      });
      return r.data;
    },
  });

  const rows = data?.data || [];
  const total = data?.pagination?.total || 0;

  return (
    <Stack spacing={2}>
      <Card>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>Stale IPFS media (V2 destinations)</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Lists NftMetadata rows whose image / animation URLs still contain ipfs:// or public gateway hosts.
            Bulk rewrite re-runs the mint metadata resolver for rows with a matching mint_confirmed pipeline row.
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} flexWrap="wrap">
            <TextField
              label="Contract address (optional)"
              size="small"
              fullWidth
              value={contract}
              onChange={(e) => { setContract(e.target.value.trim()); setPage(0); }}
              placeholder="0x…"
            />
            <TextField
              label="Collection id (optional)"
              size="small"
              fullWidth
              value={collectionId}
              onChange={(e) => { setCollectionId(e.target.value.trim()); setPage(0); }}
              placeholder="paradise-furniture-v2"
            />
            <TextField
              label="Bulk batch limit"
              size="small"
              type="number"
              value={limit}
              onChange={(e) => setLimit(Math.max(1, Math.min(2000, parseInt(e.target.value, 10) || 500)))}
              sx={{ width: 160 }}
              inputProps={{ min: 1, max: 2000 }}
            />
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => refetch()} disabled={isFetching}>
              Scan page
            </Button>
            {canBulkRewrite ? (
              <>
                <Button
                  variant="outlined"
                  disabled={dryRunMutation.isPending}
                  onClick={() => dryRunMutation.mutate()}
                >
                  {dryRunMutation.isPending ? 'Dry run…' : 'Dry run batch'}
                </Button>
                <Button
                  variant="contained"
                  color="warning"
                  disabled={rewriteMutation.isPending}
                  onClick={() => setConfirmRewrite(true)}
                >
                  Rewrite batch (limit)
                </Button>
              </>
            ) : null}
          </Stack>
          {!canBulkRewrite ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              Bulk dry-run and rewrite require super_admin. Admins can still scan paginated stale rows below.
            </Alert>
          ) : null}
          {confirmRewrite && canBulkRewrite && (
            <Alert
              severity="warning"
              sx={{ mt: 2 }}
              action={
                <Stack direction="row" spacing={1}>
                  <Button size="small" onClick={() => setConfirmRewrite(false)}>Cancel</Button>
                  <Button
                    size="small"
                    color="warning"
                    variant="contained"
                    onClick={() => {
                      setConfirmRewrite(false);
                      rewriteMutation.mutate({
                        contract: contract || undefined,
                        collectionId: collectionId || undefined,
                        limit,
                      });
                    }}
                  >
                    Run rewrite
                  </Button>
                </Stack>
              }
            >
              Re-resolve up to {limit} stale rows using the filtered contract/collection scope.
            </Alert>
          )}
        </CardContent>
      </Card>

      {isError && <Alert severity="error">{error?.response?.data?.message || error.message}</Alert>}

      {dryRunMutation.data?.dryRun && (
        <Alert severity="info">
          Dry run: batch would process up to {(dryRunMutation.data.count ?? 0)} pipeline-backed row(s).
          Sample: {(dryRunMutation.data.rows?.slice(0, 10) ?? [])
            .map((x) => `${x.contractAddress}/${x.tokenId}`)
            .join(', ') || '(none)'}
        </Alert>
      )}
      {dryRunMutation.isError && (
        <Alert severity="error">{dryRunMutation.error?.response?.data?.message || dryRunMutation.error?.message}</Alert>
      )}

      {rewriteMutation.data && !rewriteMutation.data.dryRun && (
        <Alert severity={rewriteMutation.data.failed > 0 ? 'warning' : 'success'}>
          Rewrite done — attempted: {rewriteMutation.data.attempted ?? 0}, succeeded:
          {' '}{rewriteMutation.data.succeeded ?? 0}, failed: {rewriteMutation.data.failed ?? 0}.
          {Array.isArray(rewriteMutation.data.failures) && rewriteMutation.data.failures.length > 0 && (
            <Box component="pre" sx={{ mt: 1, fontSize: 11, maxHeight: 200, overflow: 'auto' }}>
              {JSON.stringify(rewriteMutation.data.failures, null, 2)}
            </Box>
          )}
        </Alert>
      )}
      {rewriteMutation.isError && (
        <Alert severity="error">{rewriteMutation.error?.response?.data?.message || rewriteMutation.error?.message}</Alert>
      )}

      <Card>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Collection</TableCell>
                <TableCell>Contract</TableCell>
                <TableCell>Token ID</TableCell>
                <TableCell>Image</TableCell>
                <TableCell>Animation</TableCell>
                <TableCell>Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={6} align="center"><CircularProgress size={20} /></TableCell>
                </TableRow>
              )}
              {!isLoading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">No stale rows on this page.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.collectionId}</TableCell>
                  <TableCell>
                    <Tooltip title={r.contractAddress}><code>{shortAddr(r.contractAddress)}</code></Tooltip>
                  </TableCell>
                  <TableCell>{r.tokenId}</TableCell>
                  <TableCell>{truncate(r.image, 40)}</TableCell>
                  <TableCell>{truncate(r.animationUrl, 40)}</TableCell>
                  <TableCell>{formatDate(r.updatedAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_e, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100, 200]}
        />
      </Card>
    </Stack>
  );
}

export default function NftMetadataManager() {
  const { user } = useAuth();
  const allowed = user?.role === 'admin' || user?.role === 'super_admin';
  const [tab, setTab] = useState(0);

  if (!allowed) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">This page requires admin or super_admin.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>NFT Metadata</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Browse resolved NftMetadata rows in the database, find confirmed mints missing metadata,
        and run targeted or bulk backfills.
      </Typography>

      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Existing metadata" />
        <Tab label="Missing / Backfill" />
        <Tab label="Stale IPFS rewrite" />
      </Tabs>

      {tab === 0 && <ExistingMetadataTab />}
      {tab === 1 && <MissingMetadataTab />}
      {tab === 2 && <StaleIpfsRewriteTab />}
    </Box>
  );
}
